import apiClient from './apiClient';
import { isAuthenticated } from './auth';

const BRANDS_CACHE_KEY = 'catalog_brands_v2';
const BRANDS_CACHE_TIMESTAMP_KEY = 'catalog_brands_v2_timestamp';
const BRANDS_CACHE_DURATION = 30 * 60 * 1000;

const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  return (Date.now() - parseInt(timestamp, 10)) < BRANDS_CACHE_DURATION;
};

export const getCachedBrands = () => {
  try {
    const cached = sessionStorage.getItem(BRANDS_CACHE_KEY);
    const timestamp = sessionStorage.getItem(BRANDS_CACHE_TIMESTAMP_KEY);
    if (cached && isCacheValid(timestamp)) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Error reading brands cache:', error);
  }
  return null;
};

const setBrandsCache = (brands) => {
  try {
    sessionStorage.setItem(BRANDS_CACHE_KEY, JSON.stringify(brands));
    sessionStorage.setItem(BRANDS_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error saving brands cache:', error);
  }
};

export const invalidateBrandsCache = () => {
  sessionStorage.removeItem(BRANDS_CACHE_KEY);
  sessionStorage.removeItem(BRANDS_CACHE_TIMESTAMP_KEY);
};

export const getBrands = async (params = {}, ignoreCache = false) => {
  const hasFilters = params.is_national !== undefined;

  if (!hasFilters && !ignoreCache) {
    const cached = getCachedBrands();
    if (cached) {
      return cached;
    }
  }

  try {
    const headers = {};
    
    const queryParams = new URLSearchParams();
    
    if (params.is_national !== undefined) {
      queryParams.append('is_national', params.is_national);
    }
    
    const queryString = queryParams.toString();
    const url = queryString ? `/brands/?${queryString}` : '/brands/';
    
    const response = await apiClient.get(url, { headers });
    console.log('Brands response:', response.data);
    
    let brandsData;
    if (response.data && response.data.results) {
      brandsData = [...response.data.results];
      let nextPage = response.data.next;
      while (nextPage) {
        const page = await apiClient.get(nextPage, { headers });
        brandsData.push(...page.data.results);
        nextPage = page.data.next;
      }
    } else if (Array.isArray(response.data)) {
      brandsData = response.data;
    } else {
      brandsData = [];
    }
    
    const brands = brandsData.map(brand => ({
      ...brand,
      count: 0
    }));

    if (!hasFilters) {
      setBrandsCache(brands);
    }

    return brands;
  } catch (error) {
    console.error('Error fetching brands:', error);
    const cached = getCachedBrands();
    return cached || [];
  }
};

export const getNationalBrands = async () => {
  return getBrands({ is_national: true });
};

export const getInternationalBrands = async () => {
  return getBrands({ is_national: false });
};

export const getBrand = async (id) => {
  try {
    const headers = {};
    
    if (isAuthenticated()) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/brands/${id}/`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching brand:', error);
    throw error;
  }
};

export const createBrand = async (brandData) => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      throw new Error('No se encontró token de autenticación');
    }
    
    const response = await apiClient.post('/brands/', brandData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateBrandsCache();
    return response.data;
  } catch (error) {
    console.error('Error creating brand:', error);
    throw new Error(error.response?.data?.message || 'Error al crear la marca');
  }
};

export const updateBrand = async (id, brandData) => {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }
  
  const response = await apiClient.put(`/brands/${id}/`, brandData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
  invalidateBrandsCache();
  return response.data;
};

export const deleteBrand = async (id) => {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }
  
  await apiClient.delete(`/brands/${id}/`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }
  });
  invalidateBrandsCache();
};

export const applyBrandDiscount = async (id, discountData) => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      throw new Error('No se encontró token de autenticación');
    }
    
    const response = await apiClient.post(`/brands/${id}/apply-discount/`, discountData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    
    // Invalidar cachés de productos cuando se aplica descuento a marca
    localStorage.removeItem('products');
    localStorage.removeItem('productsTimestamp');
    localStorage.removeItem('admin_products');
    localStorage.removeItem('admin_productsTimestamp');
    invalidateBrandsCache();
    
    return response.data;
  } catch (error) {
    console.error('Error applying brand discount:', error);
    throw new Error(error.response?.data?.error || 'Error al aplicar descuento a la marca');
  }
};
